import Link from '~/components/Link'
import Img from '~/components/Img'
import type { EntryMeta } from '~/types'

// Vector "lock-on" reticle brackets that snap to the tile corners on hover.
const corners = [
	'left-2 top-2 border-l-2 border-t-2',
	'right-2 top-2 border-r-2 border-t-2',
	'left-2 bottom-2 border-l-2 border-b-2',
	'right-2 bottom-2 border-r-2 border-b-2',
]

// A single tile on the /work grid. Every tile is 16:9 (`aspect-video`) so it always
// has an intrinsic height and never collapses (a row-spanning tile with no height would
// collapse to a strip when dense flow places it in trailing rows with no normal
// neighbor). Featured tiles span two columns and two rows; they align exactly with two
// normal rows because WorkGrid sets the row gap to 9/16 of the column gap — see the math
// there. They stay interspersed via the grid's dense flow.
export default function WorkTile({ meta }: { meta: EntryMeta }) {
	const span = meta.featured ? 'col-span-2 row-span-2' : ''

	return (
		<li className={`m-0 ${span}`}>
			<Link
				href={`/work/${meta.slug}`}
				className="group relative block w-full aspect-video overflow-hidden bg-black transition-shadow duration-300 hover:shadow-glow-green"
			>
				{meta.cover && (
					<span className="absolute inset-0 transition duration-700 ease-out group-hover:scale-[1.07]">
						<Img
							asset={meta.cover}
							tier="thumb"
							sizes={
								meta.featured
									? '(min-width: 1024px) 50vw, 100vw'
									: '(min-width: 1024px) 25vw, (min-width: 768px) 33vw, 50vw'
							}
							alt={meta.name}
							fill
						/>
					</span>
				)}

				{/* Phosphor wash + text scrim, fades in on hover */}
				<span className="pointer-events-none absolute inset-0 bg-tile-fade opacity-0 transition-opacity duration-300 group-hover:opacity-100" />

				{/* Reticle: corner brackets snap inward and glow */}
				{corners.map((pos) => (
					<span
						key={pos}
						className={`pointer-events-none absolute h-6 w-6 scale-90 border-primary opacity-0 drop-shadow-glow-green transition-all duration-300 ease-out group-hover:scale-100 group-hover:opacity-100 ${pos}`}
					/>
				))}

				{/* Title block rises on hover, with a Bauhaus rule */}
				<span className="absolute inset-x-0 bottom-0 flex translate-y-3 flex-col p-4 opacity-0 transition-all duration-300 ease-out group-hover:translate-y-0 group-hover:opacity-100">
					<span className="font-bold leading-tight text-white">{meta.name}</span>
					<span className="my-2 h-px w-8 bg-primary" />
					<span className="text-sm text-white/70">{meta.excerpt}</span>
				</span>
			</Link>
		</li>
	)
}
